import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import type { ApiError, OrderDetail, OrderDetailResponse, RefundPreview, RefundResult } from '../api';
import { orderEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { CustomerWarnings, TagBadges } from '../components/CustomerRecord';
import { Badge, Button, CollapsiblePanel, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars, humanize, inZone } from './event/shared';
import { OrderExchange } from './OrderExchange';
import { OrderPaymentPanel, paymentStatusTone } from './OrderPayment';
import { InstallmentPlanPanel } from './InstallmentPanels';
import { dateWords } from '../terms';

/**
 * Orders (Phase 6): find an order, see what was sold, what was paid, every
 * ledger line and every event; refund all of it or some tickets.
 */

const statusTone = (status: string): Tone => {
  switch (status) {
    case 'COMPLETED':
      return 'ok';
    case 'PENDING':
    case 'MANUAL_REVIEW':
      return 'warn';
    case 'PARTIALLY_REFUNDED':
      return 'accent';
    case 'REFUNDED':
    case 'CANCELLED':
    case 'EXPIRED':
      return 'neutral';
    default:
      return 'neutral';
  }
};

const REASONS = ['CUSTOMER_REQUEST', 'EVENT_CANCELLED', 'EVENT_POSTPONED', 'DUPLICATE_PURCHASE', 'SERVICE_RECOVERY', 'FRAUD', 'OTHER'];

export default function OrdersPage() {
  const { id } = useParams();
  const can = usePermissions();
  if (!can('orders.view')) return <NotPermitted permission="orders.view" />;
  return id ? <OrderDetailView id={id} /> : <OrderList />;
}

/**
 * Notes on an order (D-169).
 *
 * Two visibilities, and the label says plainly which is which, because the
 * difference is the whole point: an internal note may say anything at all, and
 * a customer note is read by the person it is about.
 *
 * Append-only, so there is no edit and no delete here. A note is what somebody
 * thought at the time.
 */
/**
 * Put a customer on an order that has none (D-252).
 *
 * The window's walk-up form needs a **Use** press to attach what was typed, and
 * it is easy to miss: a season table went through with no name, no e-mail and
 * forty-two tickets belonging to nobody, and there was no way back.
 *
 * Only shown when the order is genuinely blank. Correcting an existing contact
 * is a different act — those tickets have been delivered to somebody and may
 * have been transferred — and the server refuses it for the same reason.
 */
function AttachContact({ orderId, onDone }: { orderId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [error, setError] = useState<string | null>(null);
  const attach = useMutation({
    mutationFn: () =>
      orderEndpoints.attachContact(orderId, {
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
      }),
    onSuccess: () => {
      setError(null);
      setOpen(false);
      onDone();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (!open) {
    return (
      <p className="mt-1 text-sm">
        <span className="text-ink-500">No customer on this order. </span>
        <button type="button" className="font-medium text-brand underline" onClick={() => setOpen(true)}>
          Add one
        </button>
      </p>
    );
  }
  return (
    <div className="mt-2 max-w-md border border-line bg-surface p-3">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div className="grid gap-2 sm:grid-cols-3">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={INPUT} />
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={INPUT} />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className={INPUT} />
      </div>
      <p className="mt-2 text-xs text-ink-500">
        The tickets on this order get this name and e-mail too, so they can be sent. A ticket already transferred to somebody else keeps its own holder.
      </p>
      <div className="mt-2 flex items-center gap-3">
        <Button onClick={() => attach.mutate()} disabled={attach.isPending || (!form.name.trim() && !form.email.trim())}>
          {attach.isPending ? 'Saving…' : 'Attach'}
        </Button>
        <button type="button" className="text-sm text-ink-500 underline" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function NotesPanel({ order, onChanged }: { order: OrderDetail; onChanged: () => void }) {
  const can = usePermissions();
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<'INTERNAL' | 'CUSTOMER'>('INTERNAL');
  const [error, setError] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => orderEndpoints.addNote(order.id, { body: body.trim(), visibility }),
    onSuccess: () => {
      setBody('');
      setError(null);
      onChanged();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const notes = order.notes ?? [];
  if (notes.length === 0 && !can('orders.note')) return null;

  return (
    <CollapsiblePanel
      title="Notes"
      storageKey="order-notes"
      summary={notes.length > 0 ? `${notes.length} note${notes.length === 1 ? '' : 's'}` : undefined}
      description="What staff said about this order, and anything written for the customer to read on their own order page. Notes are never emailed."
    >
      <div className="space-y-3 px-5 py-4">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        {notes.length === 0 ? <p className="text-sm text-ink-400">No notes yet.</p> : null}
        {notes.map((note) => (
          <div key={note.id} className="border-l-2 border-line pl-3">
            <p className="text-sm text-ink-900 whitespace-pre-wrap">{note.body}</p>
            <p className="mt-0.5 text-xs text-ink-400">
              <Badge tone={note.visibility === 'CUSTOMER' ? 'accent' : 'neutral'}>{note.visibility === 'CUSTOMER' ? 'customer can see this' : 'internal'}</Badge>{' '}
              {note.author_name ?? 'staff'} · {formatWhen(note.created_at)}
            </p>
          </div>
        ))}
        {can('orders.note') ? (
          <div className="space-y-2 border-t border-line pt-3">
            <textarea className={`${INPUT} min-h-20 w-full`} value={body} onChange={(event) => setBody(event.target.value)} placeholder="Called about the wheelchair space; sorting it Friday." maxLength={2000} />
            <div className="flex flex-wrap items-center gap-3">
              <select className={INPUT} value={visibility} onChange={(event) => setVisibility(event.target.value as 'INTERNAL' | 'CUSTOMER')}>
                <option value="INTERNAL">Internal — staff only</option>
                <option value="CUSTOMER">For the customer to read</option>
              </select>
              <Button variant="primary" disabled={add.isPending || !body.trim()} onClick={() => add.mutate()}>
                Add note
              </Button>
              {visibility === 'CUSTOMER' ? <span className="text-xs text-ink-400">Shown on their order page. It is not emailed.</span> : null}
            </div>
          </div>
        ) : null}
      </div>
    </CollapsiblePanel>
  );
}


function OrderList() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [payment, setPayment] = useState(() => new URLSearchParams(window.location.search).get('payment') ?? '');
  /*
   * The refund-request queue (D-193).
   *
   * `?refund_requested=1` has been supported by the API since requests were
   * built, and nothing ever sent it: the badge below told you an order had a
   * request once you had already opened the list it was in, which is not a
   * queue. Seeded from the URL like `payment`, so the queue is a link that can
   * live in a bookmark or on the dashboard.
   */
  const [refundRequested, setRefundRequested] = useState(() => new URLSearchParams(window.location.search).get('refund_requested') === '1');
  const can = usePermissions();
  const orders = useQuery({ queryKey: ['orders', q, status, payment, refundRequested], queryFn: () => orderEndpoints.list({ q: q || undefined, status: status || undefined, payment: payment || undefined, refund_requested: refundRequested ? '1' : undefined }) });
  return (
    <div className="space-y-4">
      <Panel
        title="Orders"
        description="Every channel. Search by order number, name or email; filter by what is still owed, or by who is waiting on a refund decision."
        actions={
          <div className="flex flex-wrap gap-2">
            <input className={INPUT} placeholder="T26-… / name / email / ticket holder" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search orders" />
            <select className={INPUT} value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              <option value="">Any status</option>
              {['PENDING', 'COMPLETED', 'PARTIALLY_REFUNDED', 'REFUNDED', 'CANCELLED', 'EXPIRED', 'MANUAL_REVIEW'].map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </select>
            <select className={INPUT} value={payment} onChange={(e) => setPayment(e.target.value)} aria-label="Payment">
              <option value="">Any payment</option>
              <option value="unpaid">Unpaid</option>
              <option value="partially_paid">Partially paid</option>
              <option value="outstanding">Balance outstanding</option>
              <option value="past_due">Past due</option>
              <option value="link_sent">Payment link sent</option>
              <option value="link_not_sent">Payment link not sent</option>
            </select>
            <label className="inline-flex items-center gap-2 px-1 text-sm text-ink-600">
              <input type="checkbox" checked={refundRequested} onChange={(e) => setRefundRequested(e.target.checked)} />
              Refund requested
            </label>
            {can('box_office.sell') ? (
              <Link to="/orders/new" className="inline-flex items-center rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90">
                New order
              </Link>
            ) : null}
          </div>
        }
      >
        {orders.error ? <ErrorNote>{(orders.error as ApiError).message}</ErrorNote> : null}
        <Table headers={['Order', 'Customer', 'Event', 'Tickets', 'Total', 'Status', 'When']}>
          {(orders.data?.orders ?? []).length === 0 ? (
            <EmptyRow columns={7}>No orders match.</EmptyRow>
          ) : (
            orders.data!.orders.map((o) => (
              <tr key={o.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <Link to={`/orders/${o.id}`} className="font-mono font-medium text-accent hover:underline">
                    {o.order_number}
                  </Link>
                  <div className="text-xs text-ink-400">{humanize(o.channel)}</div>
                </td>
                <td className="px-5 py-3">
                  <div className="text-ink-900">{o.customer_name_snapshot ?? '—'}</div>
                  <div className="text-xs text-ink-400">{o.customer_email_snapshot ?? ''}</div>
                  <TagBadges tags={o.customer_tags} />
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{o.event_titles ?? '—'}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">{o.ticket_count}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">
                  {dollars(o.cached_total_due_cents)}
                  {o.cached_refunded_cents > 0 ? <div className="text-xs text-ink-400">−{dollars(o.cached_refunded_cents)} refunded</div> : null}
                  {o.balance_due_cents && o.balance_due_cents > 0 && (o.status === 'COMPLETED' || o.status === 'PARTIALLY_REFUNDED') ? <div className="text-xs font-medium text-warn">{dollars(o.balance_due_cents)} due{o.payment_due_at ? ` ${o.payment_due_at.slice(0, 10)}` : ''}</div> : null}
                </td>
                <td className="px-5 py-3">
                  <Badge tone={statusTone(o.status)}>{o.status}</Badge>
                  {o.payment_status && o.payment_status !== 'PAID' && (o.status === 'COMPLETED' || o.status === 'PARTIALLY_REFUNDED') ? <Badge tone={paymentStatusTone(o.payment_status)}>{humanize(o.payment_status)}</Badge> : null}
                  {o.refund_requests > 0 ? <Badge tone="warn">refund requested</Badge> : null}
                  {o.transferred_count > 0 ? <Badge tone="accent">{o.transferred_count} transferred</Badge> : null}
                  {o.transfer_pending_count > 0 ? <Badge tone="neutral">{o.transfer_pending_count} transfer pending</Badge> : null}
                </td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(o.completed_at ?? o.created_at)}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

/**
 * What this refund pays back, and where it lands (D-277, D-278).
 *
 * Both of the things it says used to be worked out by hand at the counter. The
 * amount came off a calculator — face value, less the fees that do not come
 * back, less whatever an earlier partial refund already took — and was said
 * out loud before anyone could check it. And the destination was assumed: an
 * order settled with a gift card and a Visa pays back some of each, and "it'll
 * go back on your card" is wrong in a way the customer finds out about days
 * later.
 *
 * A refusal renders as a sentence, not an error: "everything refundable on
 * those items has already been refunded" is the answer to the question asked.
 */
function RefundEstimateView({ preview, pending }: { preview: RefundPreview | undefined; pending: boolean }) {
  if (pending && !preview) return <p className="text-sm text-ink-400">Working out what comes back…</p>;
  if (!preview) return null;
  if (!preview.ok) return <p className="text-sm text-ink-600">{preview.message}</p>;
  const { estimate } = preview;
  return (
    <div className={pending ? 'opacity-60' : undefined}>
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">What comes back</p>
      <p className="mt-1 text-2xl font-semibold text-ink-900">{dollars(estimate.refund_total_cents)}</p>
      {estimate.tenders.length > 0 ? (
        <ul className="mt-2 space-y-1 text-sm">
          {estimate.tenders.map((tender) => (
            <li key={tender.payment_id} className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium text-ink-900">{dollars(tender.amount_cents)}</span>
              <span className="text-ink-700">to {tender.label}</span>
              <span className="text-xs text-ink-400">{tender.note}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {estimate.kept.length > 0 ? (
        <ul className="mt-2 space-y-0.5 text-xs text-ink-600">
          {estimate.kept.map((line, index) => (
            <li key={`${line.order_item_id ?? 'order'}-${index}`}>
              Kept: {line.name} {dollars(line.amount_cents)}
            </li>
          ))}
        </ul>
      ) : null}
      {estimate.notes.map((note) => (
        <p key={note} className="mt-1 text-xs text-ink-600">
          {note}
        </p>
      ))}
    </div>
  );
}

function OrderDetailView({ id }: { id: string }) {
  const can = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refunding, setRefunding] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const [packageRefund, setPackageRefund] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [reason, setReason] = useState('CUSTOMER_REQUEST');
  const [note, setNote] = useState('');
  const [override, setOverride] = useState(false);
  const [release, setRelease] = useState(true);
  const [disposition, setDisposition] = useState<'TENDER' | 'DONATION'>('TENDER');
  const [campaignId, setCampaignId] = useState('');
  const [printerId, setPrinterId] = useState('');
  const detail = useQuery<OrderDetailResponse, ApiError>({ queryKey: ['orders', 'detail', id], queryFn: () => orderEndpoints.detail(id) });
  const effectivePrinter = printerId || detail.data?.printers[0]?.id || '';
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['orders'] });

  /*
   * Re-quoted whenever the selection, the override or the disposition changes,
   * because an estimate that lags the thing it describes is worse than none:
   * the last good number sits on screen beside a selection it does not match,
   * and it reads as current.
   */
  const refundScope = packageRefund ? 'PACKAGE' : selected.size === 0 ? 'FULL_ORDER' : 'TICKETS';
  const refundSelection = {
    scope: refundScope as 'FULL_ORDER' | 'TICKETS' | 'PACKAGE',
    ...(packageRefund ? { package_instance_id: packageRefund } : refundScope === 'TICKETS' ? { order_item_ids: [...selected].sort() } : {}),
    nonrefundable_fee_override: override,
    disposition,
  };
  const estimate = useQuery<RefundPreview, ApiError>({
    queryKey: ['orders', 'refund-estimate', id, JSON.stringify(refundSelection)],
    queryFn: () => orderEndpoints.previewRefund(id, refundSelection),
    enabled: refunding,
  });

  const refund = useMutation({
    mutationFn: () => {
      const all = selected.size === 0 && !packageRefund;
      return orderEndpoints.refund(id, { scope: packageRefund ? 'PACKAGE' : all ? 'FULL_ORDER' : 'TICKETS', ...(packageRefund ? { package_instance_id: packageRefund } : all ? {} : { order_item_ids: [...selected] }), reason_code: reason, reason_text: note.trim() || null, nonrefundable_fee_override: override, release_seats: release, disposition, donation_campaign_id: disposition === 'DONATION' ? campaignId || null : null });
    },
    onSuccess: (result) => {
      setError(null);
      setNotice(result.refund.disposition === 'DONATION' ? `Refund ${result.refund.refund_number}: ${dollars(result.refund.refund_total_cents)} converted to a gift; the customer's receipt is on their account.` : `Refund ${result.refund.refund_number} posted for ${dollars(result.refund.refund_total_cents)}${result.refund.stripe.ok ? '; Stripe refund sent' : `; STRIPE REFUND FAILED: ${result.refund.stripe.message ?? ''}`}${result.released.some((r) => !r.ok) ? '; seats were NOT released (see alerts)' : ''}.`);
      setRefunding(false);
      setPackageRefund(null);
      setSelected(new Set());
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const cancel = useMutation({
    mutationFn: () => orderEndpoints.cancel(id),
    onSuccess: refresh,
    onError: (cause: ApiError) => setError(cause.message),
  });
  /**
   * Take back credit a refund handed out (D-215).
   *
   * pos decides the figure: it voids the card and reports what was on it, so
   * the amount here can be less than the panel offered if the customer spent
   * some of it between the page loading and the button being pressed. The
   * message says what actually moved rather than what was asked for.
   */
  const reverseCredit = useMutation({
    mutationFn: ({ refundId, why }: { refundId: string; why: string }) => orderEndpoints.reverseCredit(id, refundId, { reason_code: 'OTHER', reason_text: why.trim() || null }),
    onSuccess: (result) => {
      setNotice(
        result.reversal.gift_card_ref
          ? result.reversal.reversed_cents > 0
            ? `Card ${result.reversal.gift_card_ref} voided; ${dollars(result.reversal.reversed_cents)} refunded to the card they paid with.`
            : `Card ${result.reversal.gift_card_ref} had nothing left on it, so nothing was refunded.`
          : result.reversal.reversed_cents > 0
            ? `${dollars(result.reversal.reversed_cents)} taken off their account credit and refunded to the card they paid with.`
            : 'Their account has none of that credit left, so nothing was refunded.',
      );
      void queryClient.invalidateQueries({ queryKey: ['order', id] });
    },
    onError: (cause: ApiError) => setNotice(cause.message),
  });

  const decide = useMutation({
    mutationFn: ({ refundId, approve, why }: { refundId: string; approve: boolean; why?: string }): Promise<RefundResult | { order: OrderDetail }> => (approve ? orderEndpoints.approveRefund(id, refundId, { nonrefundable_fee_override: override, release_seats: release }) : orderEndpoints.rejectRefund(id, refundId, why ?? '')),
    onSuccess: (result) => {
      setError(null);
      setNotice('refund' in result ? `Refund ${result.refund.refund_number} approved for ${dollars(result.refund.refund_total_cents)}${result.refund.stripe.ok ? '; Stripe refund sent' : `; STRIPE REFUND FAILED: ${result.refund.stripe.message ?? ''}`}.` : 'Refund request rejected.');
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const reissue = useMutation({
    mutationFn: async ({ ticketId, why }: { ticketId: string | null; why: string }): Promise<{ versions: number[] }> => {
      if (ticketId) return { versions: [(await orderEndpoints.reissue(id, ticketId, why)).reissued.credential_version] };
      return { versions: (await orderEndpoints.reissueAll(id, why)).reissued.map((r) => r.credential_version) };
    },
    onSuccess: (result) => {
      setError(null);
      setNotice(`Reissued ${result.versions.length} ticket${result.versions.length === 1 ? '' : 's'}; earlier QR codes and passes are now invalid.`);
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const resend = useMutation({
    mutationFn: (toEmail: string | null) => orderEndpoints.resendConfirmation(id, toEmail),
    onSuccess: () => {
      setError(null);
      setNotice('Confirmation queued; it shows as sent below within a minute.');
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const reprint = useMutation({
    mutationFn: async ({ ticketId, why }: { ticketId: string | null; why: string }): Promise<{ count: number }> => {
      if (ticketId) return { count: (await orderEndpoints.reprint(id, ticketId, printerId || effectivePrinter, why), 1) };
      return { count: (await orderEndpoints.reprintAll(id, printerId || effectivePrinter, why)).reprinted.length };
    },
    onSuccess: (result) => {
      setError(null);
      setNotice(`Queued ${result.count} ticket${result.count === 1 ? '' : 's'} to print; each was reissued first, so earlier copies are invalid.`);
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  /**
   * Voiding one ticket (D-170). The API refuses a ticket that was paid for and
   * not refunded, so the interesting outcome here is the message it sends back.
   *
   * Declared with the other mutations and **above every early return**: a hook
   * below one is called on some renders and not others, which is React error
   * #310 and takes the whole page with it (D-179).
   */
  /**
   * Withdraw the whole order (D-286).
   *
   * For the comped season table nobody could cancel: forty-two tickets voided
   * one at a time still left the package subscribed, and a package refund
   * refuses on a comp because there is nothing to refund. Behind a typed
   * confirmation because it releases every seat on the order at once, and
   * behind the API's own guard, which refuses outright if a cent was charged.
   */
  const voidOrder = useMutation({
    mutationFn: (reason: 'COMP_VOIDED' | 'ADMIN_VOID') => orderEndpoints.voidOrder(id, { void_reason: reason, note: 'Withdrawn from the order page', release_seats: true }),
    onSuccess: (result) => {
      const v = result.voided;
      setError(null);
      setNotice(
        `${v.order_number} withdrawn: ${v.tickets_voided.length} ticket${v.tickets_voided.length === 1 ? '' : 's'} voided, ${v.seats_released} seat${v.seats_released === 1 ? '' : 's'} back on sale` +
          (v.packages_closed > 0 ? `, ${v.packages_closed} package${v.packages_closed === 1 ? '' : 's'} closed` : '') +
          (v.subscriptions_cancelled > 0 ? ` and ${v.subscriptions_cancelled} seat subscription${v.subscriptions_cancelled === 1 ? '' : 's'} canceled` : '') +
          (v.seats_stuck > 0 ? `. ${v.seats_stuck} seat${v.seats_stuck === 1 ? ' was' : 's were'} NOT released — see alerts.` : '.'),
      );
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const voidTicket = useMutation({
    mutationFn: ({ ticketId, reason }: { ticketId: string; reason: string }) => orderEndpoints.voidTicket(id, ticketId, { void_reason: reason }),
    onSuccess: (result) => {
      setNotice(result.voided.seat_released ? 'Ticket voided and the seat put back on sale.' : 'Ticket voided. The seat was not released — check system alerts.');
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (detail.error) return <ErrorNote>{detail.error.message}</ErrorNote>;
  const order = detail.data?.order;
  if (!order || !detail.data) return null;
  const { refund_requests: requests, printers, campaigns, emails, terms_agreements: agreements } = detail.data;
  const credits = order.reversible_credits ?? [];
  // A shut panel must still report a failure, so this is counted for the header.
  const failedEmails = emails.filter((e) => e.status === 'FAILED').length;
  const canEmail = can('orders.contact.update') && (order.status === 'COMPLETED' || order.status === 'PARTIALLY_REFUNDED');
  /*
   * A REFUNDED order can still have something left to reverse (D-218): a
   * forfeited exchange difference is kept deliberately, and gating on status
   * alone hid the button on the only screen that showed the leftover.
   */
  const canRefund = can('refunds.approve') && (order.status === 'COMPLETED' || order.status === 'PARTIALLY_REFUNDED' || (order.status === 'REFUNDED' && (order.refundable_remaining_cents ?? 0) > 0));
  const canReissue = can('tickets.reissue');
  const canReprint = can('box_office.reprint') && printers.length > 0;
  const canVoid = can('tickets.void');
  const tickets = order.items.filter((i) => i.item_type === 'TICKET');
  const activeTickets = tickets.filter((i) => i.status === 'ACTIVE');
  const askReason = (what: string) => window.prompt(`Reason for the ${what} (recorded on the ticket)`);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button type="button" onClick={() => void navigate('/orders')} className="text-sm text-ink-400 hover:text-ink-900">
            ← Orders
          </button>
          <h1 className="font-mono text-xl font-semibold text-ink-900">
            {order.order_number} <Badge tone={statusTone(order.status)}>{order.status}</Badge>
          </h1>
          <p className="text-sm text-ink-600">
            {/* The organization leads when there is one (D-250): it is what is
                printed on every ticket, and the contact is who to ring. */}
            {order.customer.organization_name ? <span className="font-medium text-ink-900">{order.customer.organization_name} · </span> : null}
            {order.customer.name ?? 'Guest'} · {order.customer.email ?? ''} {order.customer.phone ? `· ${order.customer.phone}` : ''} {order.customer.sub ? '· has account' : '· guest'}
            {(order.customer.sub || order.customer.email) && can('customers.contact.view') ? (
              <Link to={`/customers/${encodeURIComponent(order.customer.sub ? `sub:${order.customer.sub}` : `email:${order.customer.email!.toLowerCase()}`)}`} className="ml-2 underline">
                Customer file
              </Link>
            ) : null}
          </p>
          {/* What staff have written about this buyer (D-358): the same warning the window shows. */}
          <CustomerWarnings record={detail.data.customer_record} />
          {/*
            Nobody on the order at all (D-252). The window's walk-up form needs
            a Use press to attach what was typed, and it is easy to miss — this
            is the way back, and it only ever fills a blank.
          */}
          {!order.customer.sub && !order.customer.name && !order.customer.email && can('orders.contact.update') ? <AttachContact orderId={order.id} onDone={refresh} /> : null}
          {order.delivery.method ? (
            <p className="text-sm text-ink-600">
              Delivery: <span className="font-medium text-ink-900">{order.delivery.name ?? order.delivery.method}</span>
              {order.delivery.address ? ` · ${[order.delivery.address.recipient_name, order.delivery.address.address_line1, order.delivery.address.address_line2, `${order.delivery.address.city}, ${order.delivery.address.region} ${order.delivery.address.postal_code}`].filter(Boolean).join(', ')}` : ''}
              {order.delivery.fulfillment_printed_at ? ` · printed ${formatWhen(order.delivery.fulfillment_printed_at)}` : order.delivery.method === 'MAIL' || order.delivery.method === 'WILL_CALL' ? ' · not printed yet (Mail & will call)' : ''}
            </p>
          ) : null}
        </div>
        <div className="flex gap-2">
          {order.status === 'PENDING' ? (
            <Button variant="danger" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              Cancel checkout
            </Button>
          ) : null}
          {canEmail ? (
            <Button
              onClick={() => {
                const to = window.prompt('Email the confirmation and tickets to', order.customer.email ?? '');
                if (to !== null) resend.mutate(to.trim() || null);
              }}
              disabled={resend.isPending}
            >
              Email tickets
            </Button>
          ) : null}
          {/*
            The same itemized receipt the customer can download (D-280), so
            somebody on the phone can read back exactly what is in front of
            them rather than a different summary of the same order.
          */}
          <Button onClick={() => window.open(`/api/admin/orders/${id}/receipt.pdf`, '_blank', 'noopener')}>Receipt (PDF)</Button>
          {canVoid && activeTickets.length > 0 ? (
            <Button
              variant="danger"
              disabled={voidOrder.isPending}
              onClick={() => {
                // Typed, not clicked: this releases every seat on the order.
                const typed = window.prompt(
                  `Withdraw the whole of ${order.order_number}?

Every live ticket is voided, every seat goes back on sale, and any package or season subscription on it is closed. No money moves — if anything was charged and not refunded, this will refuse.

Type WITHDRAW to confirm.`,
                  '',
                );
                if (typed?.trim().toUpperCase() !== 'WITHDRAW') return;
                const comp = window.confirm(`Was this a comp?

OK — a comp being withdrawn (COMP_VOIDED)
Cancel — something else (ADMIN_VOID)`);
                voidOrder.mutate(comp ? 'COMP_VOIDED' : 'ADMIN_VOID');
              }}
            >
              {voidOrder.isPending ? 'Withdrawing…' : 'Withdraw whole order'}
            </Button>
          ) : null}
          {canReissue && activeTickets.length > 0 ? (
            <Button
              onClick={() => {
                const why = askReason('reissue of every ticket');
                if (why) reissue.mutate({ ticketId: null, why });
              }}
              disabled={reissue.isPending}
            >
              Reissue all
            </Button>
          ) : null}
          {canReprint && activeTickets.length > 0 ? (
            <span className="inline-flex items-center gap-1">
              <select className={INPUT} value={effectivePrinter} onChange={(e) => setPrinterId(e.target.value)} aria-label="Printer">
                {printers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Button
                onClick={() => {
                  const why = askReason('reprint of every ticket');
                  if (why !== null) { setPrinterId(effectivePrinter); reprint.mutate({ ticketId: null, why: why || 'Reprint' }); }
                }}
                disabled={reprint.isPending}
              >
                Reprint all
              </Button>
            </span>
          ) : null}
          {canRefund && !refunding && !exchanging ? (
            <Button variant="primary" onClick={() => setRefunding(true)}>
              Refund…
            </Button>
          ) : null}
          {can('exchanges.process') && !refunding && !exchanging && activeTickets.length > 0 && (order.status === 'COMPLETED' || order.status === 'PARTIALLY_REFUNDED') ? <Button onClick={() => { setSelected(new Set()); setExchanging(true); }}>Exchange…</Button> : null}
        </div>
      </div>
      {exchanging ? (
        <OrderExchange
          order={order}
          selectedTicketIds={[...selected].map((itemId) => order.items.find((i) => i.id === itemId)?.ticket?.id).filter((id): id is string => Boolean(id))}
          canWaive={can('refunds.approve')}
          canHouseAccount={can('box_office.house_account')}
          onClose={() => { setExchanging(false); setSelected(new Set()); }}
          onDone={(message) => {
            setExchanging(false);
            setSelected(new Set());
            setNotice(message);
            refresh();
          }}
          onError={setError}
        />
      ) : null}
      {order.status === 'COMPLETED' || order.status === 'PARTIALLY_REFUNDED' || order.status === 'REFUNDED' || order.status === 'CANCELLED' ? <OrderPaymentPanel orderId={order.id} orderEmail={order.customer.email} orderName={order.customer.name} onNotice={(m) => { setError(null); setNotice(m); }} /> : null}
      <InstallmentPlanPanel orderId={order.id} onNotice={(m) => { setError(null); setNotice(m); }} />

      {credits.length > 0 && can('refunds.approve') ? (
        <Panel
          title="Credit handed out"
          description="Returned tickets were paid back as credit: on the customer’s account (or, before account credit, a gift card). Taking it back removes it and refunds the same money to the card they paid with, only what is still there, because anything already spent is gone."
        >
          <ul className="divide-y divide-line px-5 text-sm">
            {credits.map((c) => (
              <li key={c.refund_id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div>
                  <p className="font-mono font-medium text-ink-900">{c.refund_number}</p>
                  <p className="text-ink-600">
                    {c.gift_card_ref ? `${dollars(c.outstanding_cents)} still on card ${c.gift_card_ref}` : `${dollars(c.outstanding_cents)} still on their account`}
                  </p>
                </div>
                <Button
                  variant="danger"
                  disabled={reverseCredit.isPending}
                  onClick={() => {
                    if (!window.confirm(c.gift_card_ref ? `Void the gift card and refund what is left of ${dollars(c.outstanding_cents)} to the card they paid with?` : `Take ${dollars(c.outstanding_cents)} off their account credit and refund it to the card they paid with?`)) return;
                    const why = window.prompt('Why is this credit being taken back? (internal)') ?? '';
                    reverseCredit.mutate({ refundId: c.refund_id, why });
                  }}
                >
                  Take it back
                </Button>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {requests.length > 0 ? (
        <Panel title="Refund requests" description="Asked for by the customer from their order page. Approving pays it back to whatever settled the order, which is often not just a card, and puts the seats back on sale. The amount shown is worked out now, not when they asked.">
          <ul className="divide-y divide-line px-5 text-sm">
            {requests.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div>
                  <p className="font-mono font-medium text-ink-900">{r.refund_number}</p>
                  <p className="text-ink-600">
                    {r.scope === 'FULL_ORDER' ? 'Whole order' : `${r.order_item_ids.length} ticket${r.order_item_ids.length === 1 ? '' : 's'}`} · {formatWhen(r.requested_at)}
                    {r.reason_text ? ` · “${r.reason_text}”` : ''}
                  </p>
                  {/* Approving used to be pressed on a number nobody had seen. */}
                  <div className="mt-2">
                    <RefundEstimateView preview={r.preview} pending={false} />
                  </div>
                </div>
                {can('refunds.approve') ? (
                  <div className="flex gap-2">
                    <Button variant="primary" disabled={decide.isPending} onClick={() => decide.mutate({ refundId: r.id, approve: true })}>
                      Approve
                    </Button>
                    <Button
                      variant="danger"
                      disabled={decide.isPending}
                      onClick={() => {
                        const why = window.prompt('Why is this request being declined? (the customer is told)');
                        if (why) decide.mutate({ refundId: r.id, approve: false, why });
                      }}
                    >
                      Decline
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded border border-ok/30 bg-ok/10 px-3 py-2 text-sm text-ok">{notice}</p> : null}

      {refunding ? (
        <Panel title="Refund" description={packageRefund ? 'Whole package: every remaining ticket of the package and its own line, the instance closed and its seats or pass canceled.' : selected.size === 0 ? 'Whole order: every refundable line on every active item, the gift included.' : `${selected.size} selected item${selected.size === 1 ? '' : 's'}: their refundable lines only.`}>
          <div className="grid gap-4 px-5 py-4 md:grid-cols-3">
            <Field label="Reason" htmlFor="rf-reason">
              <select id="rf-reason" className={`${INPUT} w-full`} value={reason} onChange={(e) => setReason(e.target.value)}>
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {humanize(r)}
                  </option>
                ))}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="Note" htmlFor="rf-note" hint="Required for fraud, service recovery and other.">
                <input id="rf-note" className={`${INPUT} w-full`} value={note} onChange={(e) => setNote(e.target.value)} />
              </Field>
            </div>
            <div className="md:col-span-3">
              <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Where the money goes</p>
              <div className="mt-1 flex flex-wrap gap-3 text-sm text-ink-700">
                <label className="flex items-center gap-2">
                  <input type="radio" name="disposition" checked={disposition === 'TENDER'} onChange={() => setDisposition('TENDER')} /> Back to the card
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="disposition" checked={disposition === 'DONATION'} onChange={() => setDisposition('DONATION')} disabled={campaigns.length === 0} /> Convert to a gift (customer gets a receipt; nothing leaves Stripe)
                </label>
                {disposition === 'DONATION' ? (
                  <select className={INPUT} value={campaignId || campaigns.find((c) => c.default_for_refunds === 1)?.id || campaigns[0]?.id || ''} onChange={(e) => setCampaignId(e.target.value)} aria-label="Campaign">
                    {campaigns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.default_for_refunds === 1 ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink-700 md:col-span-3">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Also refund non-refundable fees (canceled event; audited as an override)
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700 md:col-span-3">
              <input type="checkbox" checked={release} onChange={(e) => setRelease(e.target.checked)} /> Put the seats back on sale
            </label>
            <div className="border-t border-line pt-4 md:col-span-3">
              <RefundEstimateView preview={estimate.data} pending={estimate.isFetching} />
            </div>
            <div className="flex gap-2 md:col-span-3">
              <Button variant="primary" onClick={() => refund.mutate()} disabled={refund.isPending}>
                {refund.isPending ? 'Refunding…' : packageRefund ? 'Refund the package' : selected.size === 0 ? 'Refund whole order' : `Refund ${selected.size} item${selected.size === 1 ? '' : 's'}`}
              </Button>
              <Button onClick={() => { setRefunding(false); setPackageRefund(null); setSelected(new Set()); }}>Cancel</Button>
            </div>
          </div>
        </Panel>
      ) : null}

      <NotesPanel order={order} onChanged={refresh} />

      {(order.accessibility_needs?.length ?? 0) > 0 || order.accessibility_note ? (
        <Panel title="Accessibility" description={`What this party asked for when they bought. It is on the door sheet for the ${dateWords().one} as well.`}>
          <div className="space-y-2 px-5 py-4 text-sm">
            {(order.accessibility_needs ?? []).map((need) => (
              <p key={need.code}>
                <span className="font-medium text-ink-900">{need.name}</span>
                <span className="block text-xs text-ink-400">{need.description}</span>
              </p>
            ))}
            {order.accessibility_note ? <p className="border-l-2 border-line pl-3 text-ink-700 italic">{order.accessibility_note}</p> : null}
          </div>
        </Panel>
      ) : null}

      <Panel title="Items" description="Face value is what reports show; the all-in column is what the customer paid for it.">
        <Table headers={[refunding || exchanging ? '' : ' ', 'Item', 'Type', 'Face', 'All-in', 'Ticket', 'Status', '']}>
          {order.items.map((item) => (
            <tr key={item.id} className="border-b border-line last:border-0">
              <td className="px-5 py-3">{((refunding && (item.item_type === 'TICKET' || item.item_type === 'DONATION' || item.item_type === 'DELIVERY')) || (exchanging && item.item_type === 'TICKET' && Boolean(item.ticket))) && item.status === 'ACTIVE' ? <input type="checkbox" checked={selected.has(item.id)} onChange={(e) => setSelected((s) => { const next = new Set(s); if (e.target.checked) next.add(item.id); else next.delete(item.id); return next; })} aria-label={`Select ${item.description_snapshot}`} /> : null}</td>
              <td className="px-5 py-3">
                <div className="font-medium text-ink-900">{item.description_snapshot}</div>
                <div className="text-xs text-ink-400">{item.performance_starts_at_snapshot ? inZone(item.performance_starts_at_snapshot, item.timezone ?? 'America/Chicago') : ''}</div>
              </td>
              <td className="px-5 py-3 text-sm text-ink-600">{item.ticket_type_name_snapshot ?? humanize(item.item_type)}</td>
              <td className="tabular px-5 py-3 text-sm text-ink-600">
                {dollars(item.face_value_cents)}
                {item.discount_cents > 0 ? <div className="text-xs text-ink-400">−{dollars(item.discount_cents)} discount</div> : null}
                {item.comp_cents > 0 ? <div className="text-xs text-ink-400">comp</div> : null}
              </td>
              <td className="tabular px-5 py-3 text-sm text-ink-600">{item.total_cents === null ? '—' : dollars(item.total_cents)}</td>
              <td className="px-5 py-3 font-mono text-xs text-ink-600">
                {item.ticket?.short_code ?? ''}
                {item.ticket ? <span className="ml-1 text-ink-400">v{item.ticket.credential_version}</span> : null}
                {item.ticket?.holder.transferred ? (
                  <div className="mt-0.5 font-sans text-xs text-accent" title="Transferred: this person holds the ticket now; reissues and emails go to them.">
                    → {item.ticket.holder.name ?? 'transferred'}
                    {item.ticket.holder.email ? ` · ${item.ticket.holder.email}` : ''}
                  </div>
                ) : null}
              </td>
              <td className="px-5 py-3">
                <Badge tone={item.status === 'ACTIVE' ? 'ok' : item.status === 'REFUNDED' ? 'neutral' : 'warn'}>{item.status}</Badge>
              </td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                {!item.ticket && (item.item_type === 'DONATION' || item.item_type === 'DELIVERY') && item.status === 'ACTIVE' && canRefund && !refunding ? (
                  <Button onClick={() => { setSelected(new Set([item.id])); setRefunding(true); }}>
                    Refund
                  </Button>
                ) : null}
                {item.item_type === 'PACKAGE' && item.status === 'ACTIVE' && item.package_instance_id && canRefund && !refunding ? (
                  <Button onClick={() => { setSelected(new Set()); setPackageRefund(item.package_instance_id!); setRefunding(true); }}>
                    Refund package
                  </Button>
                ) : null}
                {item.ticket && item.status === 'ACTIVE' ? (
                  <>
                    {canRefund && !refunding ? (
                      <Button className="mr-1" onClick={() => { setSelected(new Set([item.id])); setRefunding(true); }}>
                        Refund
                      </Button>
                    ) : null}
                    {canReissue ? (
                      <Button
                        className="mr-1"
                        disabled={reissue.isPending}
                        onClick={() => {
                          const why = askReason('reissue');
                          if (why) reissue.mutate({ ticketId: item.ticket!.id, why });
                        }}
                      >
                        Reissue
                      </Button>
                    ) : null}
                    {canReprint ? (
                      <Button
                        disabled={reprint.isPending}
                        onClick={() => {
                          const why = askReason('reprint');
                          if (why !== null) { setPrinterId(effectivePrinter); reprint.mutate({ ticketId: item.ticket!.id, why: why || 'Reprint' }); }
                        }}
                      >
                        Reprint
                      </Button>
                    ) : null}
                    {canVoid ? (
                      <Button
                        variant="danger"
                        className="ml-1"
                        disabled={voidTicket.isPending}
                        onClick={() => {
                          // Stated rather than offered as a menu: a void is not
                          // a refund, and the only safe reasons are the two the
                          // money question does not apply to (D-170).
                          const reason = window.prompt('Why is this ticket being voided?\n\nADMIN_VOID — nothing was charged for it\nDUPLICATE_ISSUE — it was issued twice\nCOMP_VOIDED — a comp being withdrawn\n\nVoiding moves no money and puts the seat back on sale.', 'ADMIN_VOID');
                          if (!reason) return;
                          voidTicket.mutate({ ticketId: item.ticket!.id, reason: reason.trim().toUpperCase() });
                        }}
                      >
                        Void
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </td>
            </tr>
          ))}
          {order.order_fees_cents > 0 ? (
            <tr className="border-b border-line last:border-0">
              <td />
              <td className="px-5 py-3 text-sm text-ink-600">Order-level fees</td>
              <td colSpan={2} />
              <td className="tabular px-5 py-3 text-sm text-ink-600">{dollars(order.order_fees_cents)}</td>
              <td colSpan={3} />
            </tr>
          ) : null}
        </Table>
        <div className="flex flex-wrap gap-6 border-t border-line px-5 py-3 text-sm">
          <span>
            Total <span className="tabular font-semibold">{dollars(order.totals.total_due_cents)}</span>
          </span>
          <span>
            Paid <span className="tabular font-semibold">{dollars(order.totals.paid_cents)}</span>
          </span>
          {order.totals.refunded_cents > 0 ? (
            <span>
              Refunded <span className="tabular font-semibold">{dollars(order.totals.refunded_cents)}</span>
            </span>
          ) : null}
          <span className="text-ink-400">
            {tickets.length} tickets · {activeTickets.length} active · business date {order.business_date ?? '—'}
          </span>
        </div>
      </Panel>

      {order.transfers.length > 0 ? (
        <Panel title="Transfers" description="Tickets offered to someone else. Once accepted, the recipient holds the ticket: their name is on it, reissues and emails go to them, and this order stays the purchase record. Search orders by the holder's name or email to get here.">
          <Table headers={['Ticket', 'To', 'Status', 'Offered', 'Outcome']}>
            {order.transfers.map((t) => {
              const item = order.items.find((i) => i.id === t.order_item_id);
              const outcome = t.accepted_at ? `accepted ${formatWhen(t.accepted_at)} · now v${t.credential_version_after ?? '?'}` : t.declined_at ? `declined ${formatWhen(t.declined_at)}` : t.cancelled_at ? `canceled ${formatWhen(t.cancelled_at)}` : t.status === 'PENDING' ? `expires ${formatWhen(t.expires_at)}` : t.status.toLowerCase();
              return (
                <tr key={t.id} className="border-b border-line last:border-0">
                  <td className="px-5 py-3 text-sm text-ink-900">{item?.description_snapshot ?? t.ticket_id}</td>
                  <td className="px-5 py-3 text-sm">
                    <div className="text-ink-900">{t.recipient_name_snapshot ?? '—'}</div>
                    <div className="text-xs text-ink-400">{t.recipient_email_snapshot ?? ''}</div>
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={t.status === 'ACCEPTED' ? 'ok' : t.status === 'PENDING' ? 'warn' : 'neutral'}>{t.status}</Badge>
                  </td>
                  <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(t.created_at)}</td>
                  <td className="px-5 py-3 text-sm text-ink-600">{outcome}</td>
                </tr>
              );
            })}
          </Table>
        </Panel>
      ) : null}

      {order.exchanges.length > 0 ? (
        <Panel title="Exchanges" description="Seats swapped at the window (D-62). The old ticket is voided and points at its replacement; the old lines are reversed and the new ones posted on one exchange journal. The difference was collected, refunded to the original tender, waived by a manager or forfeited by the customer.">
          <Table headers={['When', 'Out → In', 'Difference', 'Values', 'Money', 'By']}>
            {order.exchanges.map((x) => (
              <tr key={x.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{x.completed_at ? formatWhen(x.completed_at) : x.status}</td>
                <td className="px-5 py-3 font-mono text-sm text-ink-900">
                  {x.out.map((t) => t.short_code).join(', ')} → {x.in.map((t) => t.short_code).join(', ')}
                </td>
                <td className="px-5 py-3 text-sm">
                  <Badge tone={x.policy_code === 'WAIVE_DIFFERENCE' ? 'warn' : 'neutral'}>{humanize(x.policy_code)}</Badge>
                  {x.reason ? <div className="text-xs text-ink-400">{x.reason}</div> : null}
                </td>
                <td className="tabular px-5 py-3 text-sm">
                  {dollars(x.original_value_cents)} → {dollars(x.new_value_cents)}
                </td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">
                  {x.amount_due_cents > 0 ? `collected ${dollars(x.amount_due_cents)}` : x.amount_refunded_cents > 0 ? `refunded ${dollars(x.amount_refunded_cents)}` : x.adjustment_cents < 0 ? `waived ${dollars(-x.adjustment_cents)}` : x.adjustment_cents > 0 ? `forfeited ${dollars(x.adjustment_cents)}` : 'even'}
                  {x.fee_cents ? ` · fee ${dollars(x.fee_cents)}` : ''}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{x.staff_name ?? '—'}</td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      <Panel title="Payments">
        <Table headers={['Tender', 'Amount', 'Captured', 'Refunded', 'Card', 'Stripe', 'Status']}>
          {order.payments.length === 0 ? (
            <EmptyRow columns={7}>No payments.</EmptyRow>
          ) : (
            order.payments.map((p) => (
              <tr key={p.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">{humanize(p.tender_type)}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(p.amount_cents)}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(p.captured_amount_cents)}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(p.refunded_amount_cents)}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{p.card_brand ? `${p.card_brand} ····${p.card_last4}` : '—'}</td>
                <td className="px-5 py-3 font-mono text-xs text-ink-400">{p.stripe_payment_intent_id ?? ''}</td>
                <td className="px-5 py-3">
                  <Badge tone={p.status === 'CAPTURED' ? 'ok' : p.status === 'MANUAL_REVIEW' || p.status === 'FAILED' ? 'bad' : 'neutral'}>{p.status}</Badge>
                  {p.failure_message ? <div className="text-xs text-bad">{p.failure_message}</div> : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {/*
        What they agreed to (item 191, D-313).

        An empty list is printed as "not recorded", never as today's terms: this
        order may predate the feature entirely, and showing the current document
        beside it would assert the customer had seen words that did not exist
        when they bought.
      */}
      <CollapsiblePanel
        title="Terms agreed"
        storageKey="order-terms"
        defaultOpen={false}
        summary={agreements.length === 0 ? 'not recorded' : agreements.map((a) => `${humanize(a.document_code)} v${a.version}`).join(', ')}
      >
        {agreements.length === 0 ? (
          <p className="px-5 py-4 text-sm text-ink-600">
            No agreement is recorded for this order. Orders placed before terms were versioned have none, and there is nothing to show in place of it — the
            terms in force today are not what this customer was shown.
          </p>
        ) : (
          <Table headers={['Document', 'Version', 'Agreed', 'Where', 'From']}>
            {agreements.map((agreement) => (
              <tr key={agreement.terms_version_id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm text-ink-900">{humanize(agreement.document_code)}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">v{agreement.version}</td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(agreement.agreed_at)}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{humanize(agreement.channel)}</td>
                <td className="tabular px-5 py-3 text-xs text-ink-400">{agreement.ip_address ?? '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </CollapsiblePanel>

      {/* Reference, not the reason anyone opened this order (D-148): shut by
          default, with the count and anything failed in the header. */}
      <CollapsiblePanel
        title="Emails"
        storageKey="order-emails"
        defaultOpen={failedEmails > 0}
        summary={
          <>
            <span>
              {emails.length} message{emails.length === 1 ? '' : 's'}
            </span>
            {failedEmails > 0 ? (
              <Badge tone="bad">
                {failedEmails} failed
              </Badge>
            ) : null}
          </>
        }
        description="Everything sent to the customer about this order: confirmation with the ticket PDF, reissues, transfers, refunds and receipts. A failed message is retried by the queue, then marked failed."
      >
        <Table headers={['Sent', 'Message', 'To', 'Status']}>
          {emails.length === 0 ? (
            <EmptyRow columns={4}>No emails yet.</EmptyRow>
          ) : (
            emails.map((e) => (
              <tr key={e.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(e.sent_at ?? e.requested_at)}</td>
                <td className="px-5 py-3 text-sm text-ink-900">
                  {humanize(e.template_code)}
                  {e.subject_snapshot ? <div className="text-xs text-ink-400">{e.subject_snapshot}</div> : null}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{e.to_email}</td>
                <td className="px-5 py-3">
                  <Badge tone={e.status === 'SENT' ? 'ok' : e.status === 'FAILED' ? 'bad' : 'neutral'}>{e.status}</Badge>
                  {e.last_error && e.status !== 'SENT' ? <div className="text-xs text-bad">{e.last_error}</div> : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </CollapsiblePanel>

      {order.refunds.length > 0 ? (
        <Panel title="Refunds">
          <Table headers={['Refund', 'Scope', 'Reason', 'Amount', 'Status', 'When']}>
            {order.refunds.map((r) => (
              <tr key={r.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 font-mono text-sm">{r.refund_number}</td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {humanize(r.scope)}
                  {r.disposition === 'DONATION' ? <div className="text-xs text-accent">converted to a gift</div> : null}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {humanize(r.reason_code)}
                  {r.reason_text ? <div className="text-xs text-ink-400">{r.reason_text}</div> : null}
                  {r.rejected_reason ? <div className="text-xs text-bad">Declined: {r.rejected_reason}</div> : null}
                </td>
                <td className="tabular px-5 py-3 text-sm">{dollars(r.cached_total_refund_cents)}</td>
                <td className="px-5 py-3">
                  <Badge tone={r.status === 'COMPLETED' ? 'ok' : r.status === 'FAILED' ? 'bad' : 'neutral'}>{r.status}</Badge>
                </td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(r.completed_at ?? r.requested_at)}</td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      <CollapsiblePanel
        title="Ledger lines"
        storageKey="order-lines"
        summary={
          <span>
            {order.lines.length} line{order.lines.length === 1 ? '' : 's'}
          </span>
        }
        description="Every cent, by owner and account. Reversals point at the line they undo."
      >
        <Table headers={['Line', 'Kind', 'Owner', 'Account', 'Amount', 'Refundable', 'Date']}>
          {order.lines.length === 0 ? (
            <EmptyRow columns={7}>No lines yet (the order has not committed).</EmptyRow>
          ) : (
            order.lines.map((l) => (
              <tr key={l.id} className="border-b border-line last:border-0">
                <td className="px-5 py-2 text-sm text-ink-900">{l.name_snapshot}</td>
                <td className="px-5 py-2 text-xs text-ink-600">
                  {l.line_type}
                  {l.line_kind !== 'ORIGINAL' ? <span className="ml-1 text-accent">{l.line_kind}</span> : null}
                </td>
                <td className="px-5 py-2 text-xs text-ink-600">{l.revenue_owner_type}</td>
                <td className="px-5 py-2 text-xs text-ink-600">{l.accounting_code}</td>
                <td className={`tabular px-5 py-2 text-sm ${l.amount_cents < 0 ? 'text-bad' : ''}`}>{dollars(l.amount_cents)}</td>
                <td className="px-5 py-2 text-xs text-ink-600">{l.refundable ? 'yes' : 'no'}</td>
                <td className="px-5 py-2 text-xs text-ink-600">{l.business_date}</td>
              </tr>
            ))
          )}
        </Table>
      </CollapsiblePanel>

      <CollapsiblePanel
        title="Timeline"
        storageKey="order-timeline"
        summary={
          <span>
            {order.events.length} event{order.events.length === 1 ? '' : 's'}
            {order.events.length > 0 ? ` · last ${formatWhen(order.events[order.events.length - 1]!.occurred_at)}` : ''}
          </span>
        }
      >
        <ul className="divide-y divide-line px-5 text-sm">
          {order.events.map((e) => (
            <li key={e.id} className="flex flex-wrap gap-3 py-2">
              <span className="w-40 shrink-0 text-xs text-ink-400">{formatWhen(e.occurred_at)}</span>
              <span className="font-medium text-ink-900">{e.event_type}</span>
              <span className="text-ink-600">
                {e.actor_type.toLowerCase()}
                {e.reason ? ` · ${e.reason}` : ''}
              </span>
              {e.data_json ? <span className="font-mono text-xs text-ink-400">{e.data_json.slice(0, 120)}</span> : null}
            </li>
          ))}
        </ul>
      </CollapsiblePanel>
    </div>
  );
}
